import { useState, useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";
import { Download, Trash2, Play, Pause, Volume2, Globe, CheckSquare, RefreshCcw } from "lucide-react";
import { ShareToggle } from "@/components/ShareToggle";
import { GallerySelectionBar } from "./GallerySelectionBar";
import { ClickablePrompt } from "./ClickablePrompt";
import { useGallerySelection } from "@/hooks/useGallerySelection";
import { cn } from "@/lib/utils";
import type { User } from "@supabase/supabase-js";
import { DeleteConfirmDialog } from "./DeleteConfirmDialog";

interface SpeechItem {
  id: string;
  text: string;
  voice_id: string;
  emotion: string;
  speed: number;
  audio_url: string;
  status: string;
  is_public: boolean;
  created_at: string;
  credits_used: number;
  model?: string;
}

interface SpeechGalleryProps {
  user: User | null;
  refreshTrigger: number;
  onRetry?: (prompt: string, settings: any) => void;
}

export const SpeechGallery = ({ user, refreshTrigger, onRetry }: SpeechGalleryProps) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [speeches, setSpeeches] = useState<SpeechItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [playingId, setPlayingId] = useState<string | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const audioRefs = useRef<Map<string, HTMLAudioElement>>(new Map());

  useEffect(() => {
    fetchSpeeches();
  }, [user, refreshTrigger]);

  const fetchSpeeches = async () => {
    try {
      setLoading(true);
      
      // Use any to bypass type checking since the table was just created
      const supabaseAny = supabase as any;
      
      let query = supabaseAny
        .from("generated_speech")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(20);

      if (user) {
        query = query.eq("user_id", user.id);
      } else {
        query = query.eq("is_public", true);
      }

      const { data, error } = await query;

      if (error) throw error;
      setSpeeches(data || []);
    } catch (error) {
      console.error("Error fetching speeches:", error);
    } finally {
      setLoading(false);
    }
  };

  const handlePlay = (id: string, audioUrl: string) => {
    // Stop any currently playing audio
    if (playingId && playingId !== id) {
      const currentAudio = audioRefs.current.get(playingId);
      if (currentAudio) {
        currentAudio.pause();
        currentAudio.currentTime = 0;
      }
    }

    let audio = audioRefs.current.get(id);
    
    if (!audio) {
      audio = new Audio(audioUrl);
      audio.onended = () => setPlayingId(null);
      audio.onerror = () => {
        toast({
          title: t("speech.playError", "Playback Error"),
          description: t("speech.playErrorDesc", "Failed to play audio"),
          variant: "destructive",
        });
        setPlayingId(null);
      };
      audioRefs.current.set(id, audio);
    }

    if (playingId === id) {
      audio.pause();
      setPlayingId(null);
    } else {
      audio.play();
      setPlayingId(id);
    }
  };

  const handleDownload = async (speech: SpeechItem) => {
    try {
      const response = await fetch(speech.audio_url);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `speech_${speech.id}.mp3`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      toast({
        title: t("speech.downloadError", "Download Error"),
        description: t("speech.downloadErrorDesc", "Failed to download audio"),
        variant: "destructive",
      });
    }
  };

  const performDelete = async (id: string) => {
    try {
      const supabaseAny = supabase as any;
      const { error } = await supabaseAny
        .from("generated_speech")
        .delete()
        .eq("id", id);

      if (error) throw error;

      setSpeeches((prev) => prev.filter((s) => s.id !== id));
      
      // Stop audio if playing
      if (playingId === id) {
        const audio = audioRefs.current.get(id);
        if (audio) {
          audio.pause();
          audioRefs.current.delete(id);
        }
        setPlayingId(null);
      }

      toast({
        title: t("speech.deleteSuccess", "Deleted"),
        description: t("speech.deleteSuccessDesc", "Speech deleted successfully"),
      });
    } catch (error) {
      toast({
        title: t("speech.deleteError", "Delete Error"),
        description: t("speech.deleteErrorDesc", "Failed to delete speech"),
        variant: "destructive",
      });
      throw error;
    }
  };

  const handleDelete = (id: string) => {
    setDeleteConfirmId(id);
  };

  const confirmSingleDelete = async () => {
    if (!deleteConfirmId) return;
    setIsDeleting(true);
    try {
      await performDelete(deleteConfirmId);
    } finally {
      setIsDeleting(false);
      setDeleteConfirmId(null);
    }
  };

  // Gallery selection hook
  const {
    selectedItems,
    selectionMode,
    isBulkDeleting,
    isBulkDownloading,
    toggleSelection,
    selectAll,
    deselectAll,
    cancelSelection,
    startSelection,
    bulkDelete,
    bulkDownload,
    showBulkDeleteConfirm,
    setShowBulkDeleteConfirm,
    confirmBulkDelete,
    selectedCount,
  } = useGallerySelection({
    items: speeches.filter(s => s.status === 'completed' && s.audio_url),
    type: 'speech',
    onDelete: performDelete,
    onRefresh: () => setSpeeches(prev => [...prev]),
  });

  const handleVisibilityToggle = (id: string, newState: boolean) => {
    setSpeeches((prev) =>
      prev.map((s) => (s.id === id ? { ...s, is_public: newState } : s))
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (speeches.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <Volume2 className="h-12 w-12 mx-auto mb-4 opacity-50" />
        <p>{t("speech.noSpeeches", "No speech generated yet. Create your first one!")}</p>
      </div>
    );
  }

  return (
    <div>
      {/* Selection header */}
      {user && !selectionMode && speeches.some(s => s.status === 'completed') && (
        <div className="flex justify-end mb-4">
          <Button variant="outline" size="sm" onClick={startSelection}>
            <CheckSquare className="h-4 w-4 mr-2" />
            Select
          </Button>
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {speeches.map((speech) => (
          <div
            key={speech.id}
            className={`bg-card border border-border rounded-xl p-4 space-y-3 relative ${selectionMode && selectedItems.has(speech.id) ? 'ring-2 ring-primary' : ''}`}
          >
            {/* Selection checkbox */}
            {selectionMode && speech.status === 'completed' && speech.audio_url && (
              <div 
                className="absolute top-2 left-2 z-30"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleSelection(speech.id);
                }}
              >
                <Checkbox
                  checked={selectedItems.has(speech.id)}
                  className="h-5 w-5 bg-white/90 border-2"
                />
              </div>
            )}

            {/* Text Preview */}
            <ClickablePrompt 
              text={speech.text} 
              tab="speech"
              className="text-sm text-foreground line-clamp-3 pr-4"
            />

          {/* Badge row - consistent with other galleries */}
          <div className="flex gap-1 flex-wrap mb-2">
            {speech.status !== "completed" && (
              <Badge variant={speech.status === "processing" ? "secondary" : "destructive"} className="text-xs">
                {speech.status}
              </Badge>
            )}
            {speech.is_public && (
              <Badge variant="outline" className="text-xs bg-green-500/20 text-green-500 border-green-500/50">
                <Globe className="h-3 w-3 mr-1" />
                Public
              </Badge>
            )}
            {/* Model badge */}
            <Badge 
              variant="outline" 
              className={cn(
                "text-xs",
                speech.model === 'kokoro-82m' 
                  ? "bg-blue-500/20 text-blue-400 border-blue-500/50" 
                  : "bg-purple-500/20 text-purple-300 border-purple-500/50"
              )}
            >
              {speech.model === 'kokoro-82m' ? 'Kokoro' : 'MiniMax'}
            </Badge>
            <Badge variant="outline" className="text-xs">
              {speech.voice_id.replace(/_/g, " ")}
            </Badge>
            {speech.emotion && speech.emotion !== 'auto' && (
              <Badge variant="outline" className="text-xs">
                {speech.emotion}
              </Badge>
            )}
          </div>

          {/* Controls */}
          <div className="flex items-center justify-between pt-2 border-t border-border">
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePlay(speech.id, speech.audio_url)}
                className="rounded-full"
              >
                {playingId === speech.id ? (
                  <Pause className="h-4 w-4" />
                ) : (
                  <Play className="h-4 w-4" />
                )}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleDownload(speech)}
              >
                <Download className="h-4 w-4" />
              </Button>
              {user && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDelete(speech.id)}
                  className="text-destructive hover:text-destructive"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>

            {user && (
              <ShareToggle
                id={speech.id}
                type="speech"
                isPublic={speech.is_public}
                prompt={speech.text}
                onToggle={(newState) => handleVisibilityToggle(speech.id, newState)}
              />
            )}
          </div>
          </div>
        ))}
      </div>

      {/* Bulk action bar */}
      {selectionMode && (
        <GallerySelectionBar
          selectedCount={selectedItems.size}
          totalCount={speeches.filter(s => s.status === 'completed' && s.audio_url).length}
          onSelectAll={selectAll}
          onDeselectAll={deselectAll}
          onCancel={cancelSelection}
          onDelete={bulkDelete}
          onDownload={bulkDownload}
          isDeleting={isBulkDeleting}
          isDownloading={isBulkDownloading}
        />
      )}

      {/* Single delete confirmation dialog */}
      <DeleteConfirmDialog
        open={!!deleteConfirmId}
        onOpenChange={(open) => !open && setDeleteConfirmId(null)}
        onConfirm={confirmSingleDelete}
        title="Delete this speech?"
        description="This action cannot be undone."
        isDeleting={isDeleting}
      />

      {/* Bulk delete confirmation dialog */}
      <DeleteConfirmDialog
        open={showBulkDeleteConfirm}
        onOpenChange={setShowBulkDeleteConfirm}
        onConfirm={confirmBulkDelete}
        title={`Delete ${selectedCount} speech${selectedCount > 1 ? 'es' : ''}?`}
        description="This action cannot be undone."
        isDeleting={isBulkDeleting}
      />
    </div>
  );
};
